import type { PriceId } from '@iheartjane/types';

import { EXTRACT } from '../constants';
import { GRAMS_TO_PRICE_ID } from '../priceIdToGrams';

// EXTRACT lists the gram weights in ascending order with their display labels.
// `each` is intentionally absent — unit-priced products don't use this util.
const GRAM_PRICE_IDS = EXTRACT.map(({ id }) => id);
const GRAM_PRICE_ID_SET = new Set<string>(GRAM_PRICE_IDS);

interface WeightValueInput {
  units?: string;
  value?: number | null;
}

interface WeightRuleSection {
  bulk_weight_values?:
    ({ weight_value?: WeightValueInput | null } | null)[] | null;
  bulk_weights?: ({ price_id?: string | null } | null)[] | null;
  weight_values?: (WeightValueInput | null)[] | null;
  weights?: readonly string[] | null;
}

interface WeightRuleSet {
  excludes?: (WeightRuleSection | null)[] | null;
  includes?: (WeightRuleSection | null)[] | null;
}

/**
 * The subset of a special's `rules` this util reads. Permissive because
 * `includes`/`excludes` sit at different depths per special type: top-level,
 * under a single `qualifier`/`discountable` object, or under arrays of them.
 */
export type SpecialWeightRules = {
  discountable?: WeightRuleSet | (WeightRuleSet | null)[] | null;
  excludes?: (WeightRuleSection | null)[] | null;
  includes?: (WeightRuleSection | null)[] | null;
  qualifier?: WeightRuleSet | (WeightRuleSet | null)[] | null;
} | null;

/**
 * A brand special's serialized `applicable_weights`: a flat price-id list
 * (product specials) or `{ qualify, discount }` sides (bundle specials).
 */
export type ApplicableWeightsInput =
  | readonly string[]
  | { discount?: readonly string[] | null; qualify?: readonly string[] | null }
  | null;

export interface GetSpecialApplicableWeightsParams {
  /** Pre-resolved price-id tokens (brand specials). Takes precedence over `rules`. */
  applicableWeights?: ApplicableWeightsInput;
  /** Price ids the product carries in stock. */
  inStockWeights: readonly PriceId[];
  /** Raw store-special rules. Used when `applicableWeights` is absent. */
  rules?: SpecialWeightRules;
}

const isGramPriceId = (value: unknown): value is PriceId =>
  typeof value === 'string' && GRAM_PRICE_ID_SET.has(value);

const addPriceIds = (
  weights: readonly unknown[] | null | undefined,
  acc: Set<PriceId>
) => {
  weights?.forEach((weight) => {
    if (isGramPriceId(weight)) acc.add(weight);
  });
};

const addWeightValues = (
  weightValues: readonly (WeightValueInput | null)[] | null | undefined,
  acc: Set<PriceId>
) => {
  weightValues?.forEach((weightValue) => {
    const grams = weightValue?.value;
    const priceId = typeof grams === 'number' && GRAMS_TO_PRICE_ID.get(grams);
    if (priceId) acc.add(priceId);
  });
};

const addSectionWeights = (section: WeightRuleSection, acc: Set<PriceId>) => {
  addPriceIds(section.weights, acc);
  addWeightValues(section.weight_values, acc);
  addPriceIds(
    section.bulk_weights?.map((bulk) => bulk?.price_id),
    acc
  );
  addWeightValues(
    section.bulk_weight_values?.map((bulk) => bulk?.weight_value ?? null),
    acc
  );
};

type WalkContext = 'includes' | 'excludes' | 'other';

// Recursively walks the rules tree collecting weights into the includes/excludes
// accumulators. `includes`/`excludes` sections live at different depths per
// special type (top-level, or nested under `qualifier`/`discountable`), so
// rather than special-casing each layout we descend through everything and let
// `context` track how we arrived at the current node:
//   - 'includes'/'excludes': this node is a section — pull its weights into the
//     matching accumulator.
//   - 'other': a wrapper we're only passing through (root, qualifier, settings,
//     a nested `kinds` entry, etc.) — don't extract, just keep descending.
// The context flips to 'includes'/'excludes' only when we recurse into a key by
// that name; every other key stays 'other'.
const walkRules = (
  node: unknown,
  context: WalkContext,
  includesAcc: Set<PriceId>,
  excludesAcc: Set<PriceId>
) => {
  if (Array.isArray(node)) {
    node.forEach((item) => walkRules(item, context, includesAcc, excludesAcc));
    return;
  }
  if (!node || typeof node !== 'object') return;

  if (context === 'includes') addSectionWeights(node, includesAcc);
  else if (context === 'excludes') addSectionWeights(node, excludesAcc);

  for (const [key, value] of Object.entries(node)) {
    const childContext =
      key === 'includes' || key === 'excludes' ? key : 'other';
    walkRules(value, childContext, includesAcc, excludesAcc);
  }
};

const normalizeApplicableWeights = (
  applicableWeights: ApplicableWeightsInput | undefined
): PriceId[] | null => {
  if (applicableWeights == null) return null;
  if (Array.isArray(applicableWeights))
    return applicableWeights.filter(isGramPriceId);

  // `Array.isArray` won't narrow the readonly-array member out of the union.
  const { qualify, discount } = applicableWeights as {
    discount?: readonly string[] | null;
    qualify?: readonly string[] | null;
  };
  return [...(qualify ?? []), ...(discount ?? [])].filter(isGramPriceId);
};

const weightsFromRules = (
  rules: SpecialWeightRules | undefined
): Set<PriceId> => {
  const includes = new Set<PriceId>();
  const excludes = new Set<PriceId>();
  if (rules) walkRules(rules, 'other', includes, excludes);

  // No include restriction means the special covers every weight.
  const applicable = includes.size
    ? includes
    : new Set<PriceId>(GRAM_PRICE_IDS);
  excludes.forEach((priceId) => applicable.delete(priceId));
  return applicable;
};

/**
 * Resolves the weights a special applies to for a product, as display labels
 * (e.g. `['1g', '3.5g', '7g']`), narrowed to the weights the product carries in
 * stock. A special with no weight rules applies to every in-stock weight.
 *
 * Pass a store special's `rules` or a brand special's `applicableWeights`. It
 * handles all three weight shapes — canonical `weight_values`, legacy `weights`,
 * and computed `applicable_weights` — and answers only "which weights does this
 * apply to", not a cart's qualifying status.
 */
export const getSpecialApplicableWeights = ({
  applicableWeights,
  inStockWeights,
  rules,
}: GetSpecialApplicableWeightsParams): string[] => {
  const inStock = new Set<PriceId>(inStockWeights.filter(isGramPriceId));
  if (inStock.size === 0) return [];

  const preResolved = normalizeApplicableWeights(applicableWeights);
  const applicable = preResolved
    ? new Set(preResolved)
    : weightsFromRules(rules);

  return EXTRACT.filter(({ id }) => applicable.has(id) && inStock.has(id)).map(
    ({ label }) => label
  );
};
